/**
 * Sub-nav for Argument Builder: rhetorician's flow.
 * An intellectual proving ground — not a persuasion configuration screen.
 *
 * 1. Claim Formation — What are you asserting? What are you trying to move?
 * 2. Structural Integrity — Pillars: distinct, causal, hierarchical?
 * 3. Opposition Mapping — Who disagrees? Their strongest version? Your weak assumption?
 * 4. Rebuttal Engineering — Counter-positioning: reframe, elevate, collapse
 * 5. Rhetorical Mutation — Where methodologies fit; argument under different forces
 *
 * Mobile (<md): collapses to a native <select> dropdown — no horizontal scrolling.
 * Desktop (md+): full horizontal tab row with phase label strip.
 */

import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Clock, FileCheck, FileText, Quote, Target } from 'lucide-react'
import { FC } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

const PHASES: {
    phase: string
    tagline: string
    steps: { id: string; label: string; path: string; icon: typeof FileText }[]
}[] = [
    {
        phase: 'Situation Builder',
        tagline: "Define the moments, setup, and core claim for your enhanced brief",
        steps: [
            { id: 'situations', label: 'Decision Moments', path: '/argument-builder/situations', icon: Clock },
            { id: 'encounter', label: 'The Setup', path: '/argument-builder/encounter', icon: Target },
            { id: 'thesis', label: 'Your Claim', path: '/argument-builder/thesis', icon: Quote },
            { id: 'objections', label: 'Objections & Proof', path: '/argument-builder/objections', icon: AlertTriangle },
            { id: 'outcomes', label: 'Enhanced Briefs', path: '/argument-builder/outcomes', icon: FileCheck },
        ],
    },
]

const ALL_ITEMS = PHASES.flatMap(p => p.steps)

export const ArgumentBuilderSubNav: FC = () => {
    const navigate = useNavigate()
    const { runId } = useParams<{ runId: string }>()
    const location = useLocation()
    const base = `/dashboard/persuasion-lab/${runId || ''}`

    const currentId = (() => {
        const path = location.pathname
        // Check for specific steps first to avoid greedy matching
        const steps = PHASES[0].steps
        for (const item of steps) {
            if (path.includes(item.path)) return item.id
        }
        return 'situations'
    })()

    const currentItem = ALL_ITEMS.find(i => i.id === currentId) ?? ALL_ITEMS[0]
    void currentItem

    if (!runId) return null

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const item = ALL_ITEMS.find(i => i.id === e.target.value)
        if (item) navigate(`${base}${item.path}`)
    }

    const phaseForCurrent = PHASES.find(p => p.steps.some(s => s.id === currentId))

    return (
        /* sticky top-[120px] = app outer header (64px) + CampaignPageHeader (~56px) */
        <div className='bg-white border-b border-gray-200 sticky top-[120px] z-20 shadow-sm'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6'>

                {/* Mobile: native select dropdown */}
                <div className='md:hidden py-2'>
                    <div className='relative'>
                        <select
                            value={currentId}
                            onChange={handleSelectChange}
                            className='w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500'
                        >
                            {ALL_ITEMS.map(item => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                        </select>
                        <ChevronDown className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                    </div>
                    {phaseForCurrent && (
                        <p className='mt-1 text-[11px] uppercase tracking-widest font-semibold text-violet-600'>
                            {phaseForCurrent.phase}
                        </p>
                    )}
                </div>

                {/* Desktop: full horizontal tab row */}
                <nav className='hidden md:flex items-center gap-1 py-3'>
                    {PHASES.map(({ phase, steps }, phaseIdx) => (
                        <div key={phase} className='flex items-center gap-1'>
                            {phaseIdx > 0 && (
                                <span className='mx-1 text-gray-300' aria-hidden>
                                    <ChevronRight className='w-3.5 h-3.5' />
                                </span>
                            )}
                            {steps.map(item => (
                                <NavButton
                                    key={item.id}
                                    base={base}
                                    item={item}
                                    isActive={currentId === item.id}
                                    onNavigate={navigate}
                                />
                            ))}
                        </div>
                    ))}
                </nav>

                {/* Phase label strip — desktop only */}
                {phaseForCurrent && (
                    <div className='hidden md:block pb-2 pt-0.5'>
                        <p className='text-[11px] uppercase tracking-widest font-semibold text-violet-600'>
                            {phaseForCurrent.phase}
                        </p>
                        <p className='text-[12px] text-gray-500 mt-0.5'>{phaseForCurrent.tagline}</p>
                    </div>
                )}

            </div>
        </div>
    )
}

function NavButton({
    base,
    item,
    isActive,
    onNavigate,
}: {
    base: string
    item: { id: string; label: string; path: string; icon: typeof FileText }
    isActive: boolean
    onNavigate: (url: string) => void
}) {
    const Icon = item.icon
    const href = `${base}${item.path}`
    return (
        <button
            type='button'
            onClick={() => onNavigate(href)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                isActive
                    ? 'bg-violet-100 text-violet-900 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
        >
            <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-violet-600' : ''}`} />
            <span>{item.label}</span>
            {isActive && <CheckCircle2 className='w-3.5 h-3.5 text-violet-600 shrink-0' />}
        </button>
    )
}
